import DeckGL from '@deck.gl/react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { Colors } from '../../constants/colors';
import { DEFAULT_MODE, MODES } from '../../constants/map-modes';
import Floor from '../../models/Floor';
import * as messageNotifier from '../../services/MessageNotifier';
import { floorsSet } from '../../store/actions/floorActions';
import { mapGet, mapsSet, viewportSet } from '../../store/actions/mapActions';
import { polygonsSet } from '../../store/actions/polygonActions';
import { selectedFloorSelector } from '../../store/selectors/floorSelectors';
import {
  focusedStoreSelector,
  isEditableSelector,
  isMapDrawModeSelector,
  mapViewPortSelector
} from '../../store/selectors/mapSelectors';
import useAtmLayer from './layers/useAtmLayer';
import useFloorLayer from './layers/useFloorLayer';
import useGeoJsonLayer from './layers/useGeoJsonLayer';
import useMobileGeoJsonLayer from './layers/useMobileGeoJsonLayer';
import usePathLayer from './layers/usePathLayer';
import useResidentLogoLayer from './layers/useResidentLogoLayer';
import useResidentNameLayer from './layers/useResidentNameLayer';
import useStretchResidentInfoLayer from './layers/useStretchResidentInfoLayer';
import MapActionListener from './MapActionListener';
import FloorsMenu from './Menu/Floors';
import MapModes from './Menu/MapModes';

const MapModesContainer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: 13rem;
  background-color: ${Colors.menuBackground};
`;

const Container = styled.div`
  // #deckgl-wrapper {
  //   background: ${Colors.yankeesBlue};
  // }
`;

const Overlay = styled.div`
  position: fixed;
  width: 100%;
  height: 100%;
  z-index: 1;
`;

const ModalWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  right: 0;
  width: fit-content;
  z-index: 1000;

  transform: translateX(-50%) translateY(-120%);

  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  border-radius: 2px;
  border: 0.5px solid rgba(201, 201, 201, 1);
  box-shadow: 0 0 0 0.5px rgba(201, 201, 201, 1),
    4px 4px 12px 0 rgba(0, 0, 0, 0.03);
  opacity: 1;
  background-color: rgba(255, 255, 255, 1);
  padding: 20px;

  .triangle {
    position: absolute;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%) translateY(95%);
  }

  .logo {
    width: 75px;
    height: 50px;
    margin-bottom: 10px;
  }

  a {
    border-radius: 2px;
    border: 0.5px solid rgba(0, 0, 0, 1);
    opacity: 1;
    background: transparent;
    opacity: 1;
    color: rgba(3, 3, 3, 1);
    font-size: 12px;
    font-weight: 500;
    font-style: Medium;
    letter-spacing: 0px;
    text-align: right;
    line-height: 24px;
    text-decoration: none;
    width: 232px;
    text-align: center;
    padding: 3px 0;
  }

  p {
    margin: 0;
  }

  .p-margin {
    margin: 10px 0;
  }
`;

const Close = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  cursor: pointer;
  color: #000;
  font-size: 14px;
`;

const MapContainer = () => {
  const dispatch = useDispatch();
  const focusStore = useSelector(focusedStoreSelector);
  const activeFloor = useSelector(selectedFloorSelector);
  const isMapDrawMode = useSelector(isMapDrawModeSelector);
  const isEditable = useSelector(isEditableSelector);
  const viewport = useSelector(mapViewPortSelector);
  const [openModal, setOpenModal] = useState(false);
  const hasBootstrappedRef = useRef(false);
  const mapGetTimeoutRef = useRef(null);
  const hydrateStartRef = useRef(null);
  const mapGetStartRef = useRef(null);
  const lastLoadModeRef = useRef('unknown');

  const [activeMode, setActiveMode] = useState(DEFAULT_MODE);
  const selectedMode = useMemo(
    () => MODES.find(({ key }) => key === activeMode),
    [activeMode]
  );

  const editableGeoJsonLayer = useGeoJsonLayer(selectedMode);
  const floorLayer = useFloorLayer(activeFloor);
  const [
    mobileGeoJsonLayer,
    isMobileGeoJsonLayerVisible
  ] = useMobileGeoJsonLayer();
  const residentNameLayer = useResidentNameLayer();
  const residentLogoLayers = useResidentLogoLayer();
  const pathLayer = usePathLayer();
  const [
    stretchLogoLayer,
    isStretchLogoLayerVisible
  ] = useStretchResidentInfoLayer();
  const atmLayer = useAtmLayer();

  const normalizeFloors = floorResults =>
    (floorResults ?? [])
      .filter(floor => !floor.name?.includes('2D'))
      .map(
        floor =>
          new Floor(
            floor.id,
            floor.name,
            floor.number_representation,
            floor.map_image
          )
      )
      .sort((a, b) => a.id - b.id);

  const hydrateMapData = async payload => {
    if (!payload || hasBootstrappedRef.current) {
      return;
    }

    const floors = normalizeFloors(payload.floors || payload.floorsData);
    if (!floors.length) {
      return;
    }

    hasBootstrappedRef.current = true;
    hydrateStartRef.current = performance.now();
    lastLoadModeRef.current = 'bootstrap';

    await floors[0].generateCanvasImage();

    dispatch(floorsSet(floors));
    dispatch(polygonsSet(payload.polygons || payload.residentPolygons || []));
    dispatch(
      mapsSet({
        maps: payload.maps || payload.mapsData || [],
        floors
      })
    );
    messageNotifier.fireReadyEvent();
  };

  const readBootstrapData = eventData => {
    if (!eventData) {
      return null;
    }

    if (typeof eventData === 'string') {
      if (eventData === 'ready' || eventData === 'floorReady') {
        return null;
      }
      try {
        return JSON.parse(eventData);
      } catch {
        return null;
      }
    }

    return eventData;
  };

  useEffect(() => {
    const lng = new URLSearchParams(window.location.search).get('lng');
    localStorage.setItem('lang', lng || 'sr');

    const shouldSkipMapGet =
      new URLSearchParams(window.location.search).get('bootstrap') === '1';

    hydrateMapData(window.__MAP_BOOTSTRAP__);
    if (!hasBootstrappedRef.current && !shouldSkipMapGet) {
      mapGetTimeoutRef.current = setTimeout(() => {
        if (!hasBootstrappedRef.current) {
          mapGetStartRef.current = performance.now();
          lastLoadModeRef.current = 'mapGet';
          dispatch(mapGet());
        }
      }, 400);
    }

    const handleMessage = event => {
      const data = readBootstrapData(event.data);
      if (!data || data.command !== 'bootstrapMap') {
        return;
      }

      if (mapGetTimeoutRef.current) {
        clearTimeout(mapGetTimeoutRef.current);
      }
      hydrateMapData(data.payload || data.data);
    };

    window.addEventListener('message', handleMessage);
    return () => {
      if (mapGetTimeoutRef.current) {
        clearTimeout(mapGetTimeoutRef.current);
      }
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const lng = new URLSearchParams(window.location.search).get('lng');

  const renderMenu = () => (
    <MapModesContainer>
      <FloorsMenu />
      {isMapDrawMode && (
        <>
          <MapModes onChange={setActiveMode} activeMode={activeMode} />
        </>
      )}
    </MapModesContainer>
  );

  const layers = useMemo(
    () => [
      floorLayer,
      isEditable && editableGeoJsonLayer,
      isMobileGeoJsonLayerVisible && mobileGeoJsonLayer,
      residentNameLayer,
      residentLogoLayers,
      isStretchLogoLayerVisible && stretchLogoLayer,
      atmLayer,
      ...pathLayer
    ],
    [
      editableGeoJsonLayer,
      floorLayer,
      isEditable,
      isMobileGeoJsonLayerVisible,
      isStretchLogoLayerVisible,
      mobileGeoJsonLayer,
      pathLayer,
      residentLogoLayers,
      stretchLogoLayer,
      residentNameLayer,
      atmLayer
    ]
  );

  useEffect(
    () => {
      if (floorLayer) {
        messageNotifier.floorLoadedEvent();
      }
    },
    [floorLayer]
  );

  const onViewStateChange = ({ viewState }) => {
    dispatch(viewportSet(viewState));
  };

  return (
    <>
      {openModal &&
        !window.ReactNativeWebView && (
        <Overlay onClick={() => setOpenModal(false)}>
          <ModalWrapper>
            <img
              className="logo"
              src={focusStore?.other?.logo_black || focusStore?.logo}
              alt="Logo"
            />
            <img
              className="triangle"
              src="/images/triangle.svg"
              alt="arrow"
            />
            <p
              style={{ color: '#000000', fontWeight: 500, fontSize: '10px' }}
            >
              {focusStore?.other?.floors
                ?.map(floor => floor?.name?.toUpperCase())
                ?.join(' • ')}
            </p>
            <p
              style={{ color: '#989898', fontWeight: 400, fontSize: '10px' }}
              className="p-margin"
            >
              {focusStore?.other?.districts?.[0]?.name
                ? `${focusStore?.other.districts[0].name
                  .toLowerCase()
                  .split(' ')
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ')}`
                : ''}
              {focusStore?.other?.lobbies?.[0]?.name
                ? ` •  {lng === 'sr' ? 'Lobi' : 'Lobby'} ${
                  focusStore.other.lobbies[0].name
                }`
                : ''}
            </p>

            <a
              target="_blank"
              rel="noopener noreferrer"
              href={`https://galerijabelgrade.com/${focusStore?.type}/${
                  focusStore?.residentSlug
              }`}
            >
              {lng === 'sr' ? 'VIŠE INFORMACIJA' : 'MORE INFO'}
            </a>

            <Close onClick={() => setOpenModal(false)}>x</Close>
          </ModalWrapper>
        </Overlay>
      )}
      <Container>
        <MapActionListener
          mode={selectedMode}
          setModalPosition={() => {
            setOpenModal(true);
          }}
        >
          <DeckGL
            getCursor={() => 'default'}
            initialViewState={viewport}
            controller={{ touchRotate: true }}
            viewState={viewport}
            layers={layers}
            onViewStateChange={onViewStateChange}
          />
        </MapActionListener>

        {isEditable && renderMenu()}
      </Container>
    </>
  );
};

export default MapContainer;
